import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import CircularProgress from '@material-ui/core/CircularProgress'
import {
  BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Cell,
  ResponsiveContainer, Legend,
} from 'recharts'

var SVC_COLORS = { stt: '#22D3EE', tts: '#FB7185', livekit: '#60A5FA', llm: '#34D399', other: '#94A3B8' }
var SVC_SHORT = { stt: 'STT', tts: 'TTS', livekit: 'LiveKit', llm: 'LLM', other: 'Other' }
var SVC_LABELS = { stt: 'Speech Recognition', tts: 'Voice Synthesis', livekit: 'LiveKit', llm: 'Language Model', other: 'Other' }

var useStyles = makeStyles(function(theme) {
  return {
    section: { marginBottom: theme.spacing(3) },
    card: { padding: '20px 24px', border: '1px solid #e8ecf4', borderRadius: 12 },
    cardTitle: { fontSize: 14, fontWeight: 700, color: '#1a2540', marginBottom: 4 },
    cardSub: { fontSize: 12, color: '#555', marginBottom: 16 },
    kpiCard: {
      padding: '18px 20px',
      border: '1px solid #e8ecf4',
      borderRadius: 12,
    },
    kpiIcon: {
      width: 32, height: 32, borderRadius: 9, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      marginBottom: 10,
    },
    kpiLabel: { fontSize: 11, fontWeight: 700, color: '#444', textTransform: 'uppercase', letterSpacing: '0.06em' },
    kpiValue: { fontSize: 26, fontWeight: 800, fontFamily: 'monospace', marginTop: 4 },
    kpiSub: { fontSize: 11, color: '#555', marginTop: 4 },
    th: {
      fontSize: 11, fontWeight: 700, color: '#555', textTransform: 'uppercase',
      letterSpacing: '0.06em', padding: '10px 16px', background: '#fafafa',
      textAlign: 'left', whiteSpace: 'nowrap',
    },
    td: { padding: '12px 16px', fontSize: 13 },
    trHover: { borderBottom: '1px solid #f0f0f0', '&:hover': { background: '#f8faff' } },
  }
})

function fmtCost(v) {
  if (v == null || v === 0) return '$0.0000'
  if (v < 0.01) return '$' + v.toFixed(5)
  return '$' + v.toFixed(4)
}

function fmtMs(ms) {
  if (!ms) return '—'
  if (ms >= 1000) return (ms / 1000).toFixed(2) + 's'
  return Math.round(ms) + 'ms'
}

function fmtDur(min) {
  if (!min) return '0s'
  if (min < 1) return Math.round(min * 60) + 's'
  return min.toFixed(1) + ' min'
}

function KPITile(props) {
  var classes = useStyles()
  return (
    <Paper className={classes.kpiCard} elevation={0}>
      <div className={classes.kpiIcon} style={{ background: props.color + '18', border: '1px solid ' + props.color + '30' }}>
        <span style={{ fontSize: 16 }}>{props.icon}</span>
      </div>
      <Typography className={classes.kpiLabel}>{props.label}</Typography>
      <Typography className={classes.kpiValue} style={{ color: props.color }}>{props.value}</Typography>
      {props.sub && <Typography className={classes.kpiSub}>{props.sub}</Typography>}
    </Paper>
  )
}

function ChartTooltip(props) {
  if (!props.active || !props.payload || !props.payload.length) return null
  return (
    <div style={{ background: '#fff', border: '1px solid #e8ecf4', borderRadius: 8, padding: '8px 12px', boxShadow: '0 4px 16px rgba(99,102,241,0.1)' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#888', marginBottom: 3 }}>{props.label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#1a2540' }}>
        {props.formatter ? props.formatter(props.payload[0].value) : props.payload[0].value}
      </div>
    </div>
  )
}

export default function AnalyticsTab(props) {
  var classes = useStyles()
  var data = props.data || {}
  var loading = props.loading

  var kpis = data.kpis || {}
  var svcLatencies = data.svcLatencies || {}
  var svcTotals = data.svcTotals || {}
  var sessions = data.sessions || []
  var dailySeries = data.dailySeries || []
  var hourlyActivity = data.hourlyActivity || []
  var weekdayActivity = data.weekdayActivity || []
  var costDistribution = data.costDistribution || []

  var totalCost = kpis.totalCost || 0
  var totalSessions = kpis.totalSessions || 0
  var totalTurns = kpis.totalTurns || 0
  var avgCostPerTurn = totalTurns > 0 ? totalCost / totalTurns : 0

  // Latency comparison bar chart data
  var latencyData = [
    { label: 'STT', ms: svcLatencies.stt && svcLatencies.stt.avgMs || 0, color: '#22D3EE' },
    { label: 'LLM', ms: svcLatencies.llm && svcLatencies.llm.avgMs || 0, color: '#34D399' },
    { label: 'TTS', ms: svcLatencies.tts && svcLatencies.tts.avgMs || 0, color: '#FB7185' },
  ].filter(function(d) { return d.ms > 0 })

  // Service breakdown rows
  var svcRows = Object.entries(svcTotals)
    .filter(function(e) { return e[1] > 0 })
    .sort(function(a, b) { return b[1] - a[1] })
    .map(function(e) {
      var k = e[0], v = e[1]
      return {
        key: k,
        label: SVC_LABELS[k] || k,
        color: SVC_COLORS[k] || '#94A3B8',
        cost: v,
        pct: totalCost > 0 ? (v / totalCost * 100) : 0,
        avgPerSession: totalSessions > 0 ? v / totalSessions : 0,
        sessionsUsed: sessions.filter(function(s) { return (s.serviceCosts && s.serviceCosts[k] > 0) }).length,
      }
    })

  // Peak hour
  var maxHourCount = Math.max.apply(null, hourlyActivity.map(function(h) { return h.count })) || 0
  var peakHour = maxHourCount > 0 ? hourlyActivity.find(function(h) { return h.count === maxHourCount }) : null
  var peakHourLabel = peakHour ? peakHour.hour + ':00 – ' + (peakHour.hour + 1) + ':00' : '—'

  return (
    <div>
      {/* KPI row */}
      <div className={classes.section}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <KPITile icon="⏱" label="Avg Conversation Duration" value={loading ? '…' : fmtDur(kpis.avgDurationMin)} color="#818CF8" sub="avg time spent per conversation" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KPITile icon="💬" label="Avg Turns / Conversation" value={loading ? '…' : (kpis.avgTurnsPerSession || 0).toFixed(1)} color="#34D399" sub="voice exchanges per conversation" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KPITile icon="💰" label="Cost / Turn" value={loading ? '…' : fmtCost(avgCostPerTurn)} color="#FB7185" sub="avg cost per voice exchange" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KPITile icon="🎙" label="Total Audio Minutes" value={loading ? '…' : (kpis.totalAudioMin || 0).toFixed(1) + ' min'} color="#22D3EE" sub="across all conversations" />
          </Grid>
        </Grid>
      </div>

      {/* Cost charts row */}
      <div className={classes.section}>
        <Grid container spacing={2}>
          {/* Stacked area chart */}
          <Grid item xs={12} md={8}>
            <Paper className={classes.card} elevation={0}>
              <Typography className={classes.cardTitle}>Cost Over Time</Typography>
              <Typography className={classes.cardSub}>Daily cost by service</Typography>
              {loading ? (
                <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={dailySeries} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      {Object.keys(SVC_COLORS).map(function(k) {
                        return (
                          <linearGradient key={k} id={'ag-' + k} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={SVC_COLORS[k]} stopOpacity={0.3} />
                            <stop offset="95%" stopColor={SVC_COLORS[k]} stopOpacity={0.02} />
                          </linearGradient>
                        )
                      })}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#555' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#555' }} tickFormatter={function(v) { return '$' + v.toFixed(3) }} width={60} />
                    <Tooltip formatter={function(v, name) { return ['$' + Number(v).toFixed(5), name] }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {['llm', 'stt', 'tts', 'livekit', 'other'].map(function(k) {
                      return (
                        <Area key={k} type="monotone" dataKey={k} name={SVC_SHORT[k]} stackId="1"
                          stroke={SVC_COLORS[k]} fill={'url(#ag-' + k + ')'} strokeWidth={1.5} />
                      )
                    })}
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          {/* Cost distribution */}
          <Grid item xs={12} md={4}>
            <Paper className={classes.card} elevation={0}>
              <Typography className={classes.cardTitle}>Cost Distribution</Typography>
              <Typography className={classes.cardSub}>Conversations by cost bucket</Typography>
              {loading ? (
                <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </div>
              ) : costDistribution.length === 0 ? (
                <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#777', fontSize: 12 }}>No data</div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={costDistribution} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="histGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#818CF8" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#A78BFA" stopOpacity={0.55} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#f0f0f0" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#555' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#555' }} axisLine={false} tickLine={false} width={25} />
                    <Tooltip content={<ChartTooltip formatter={function(v) { return v + ' conversations' }} />} />
                    <Bar dataKey="count" fill="url(#histGrad)" radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      </div>

      {/* Activity patterns row */}
      <div className={classes.section}>
        <Grid container spacing={2}>
          {/* Peak hours */}
          <Grid item xs={12} md={6}>
            <Paper className={classes.card} elevation={0}>
              <Typography className={classes.cardTitle}>Peak Hours</Typography>
              <Typography className={classes.cardSub}>
                Conversations by hour of day{maxHourCount > 0 ? ' · busiest at ' + peakHourLabel : ''}
              </Typography>
              {loading ? (
                <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={22} /></div>
              ) : (
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={hourlyActivity} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke="#f0f0f0" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="hour" tick={{ fontSize: 9, fill: '#555' }} axisLine={false} tickLine={false}
                      tickFormatter={function(h) { return h % 4 === 0 ? h + 'h' : '' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#555' }} axisLine={false} tickLine={false} width={22} />
                    <Tooltip content={<ChartTooltip formatter={function(v) { return v + ' conversations' }} />}
                      labelFormatter={function(h) { return h + ':00 – ' + (h + 1) + ':00' }} />
                    <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                      {hourlyActivity.map(function(entry, i) {
                        var isPeak = entry.count === maxHourCount && entry.count > 0
                        return <Cell key={i} fill={isPeak ? '#818CF8' : 'rgba(99,102,241,0.25)'} />
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          {/* Day of week */}
          <Grid item xs={12} md={6}>
            <Paper className={classes.card} elevation={0}>
              <Typography className={classes.cardTitle}>Day of Week</Typography>
              <Typography className={classes.cardSub}>Conversations by weekday</Typography>
              {loading ? (
                <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={22} /></div>
              ) : (function() {
                var maxCount = Math.max.apply(null, weekdayActivity.map(function(d) { return d.count })) || 0
                return (
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart data={weekdayActivity} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                      <CartesianGrid stroke="#f0f0f0" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#555' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: '#555' }} axisLine={false} tickLine={false} width={22} />
                      <Tooltip content={<ChartTooltip formatter={function(v) { return v + ' conversations' }} />} />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        {weekdayActivity.map(function(entry, i) {
                          var isPeak = entry.count === maxCount && entry.count > 0
                          return <Cell key={i} fill={isPeak ? '#34D399' : 'rgba(52,211,153,0.28)'} />
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )
              })()}
            </Paper>
          </Grid>
        </Grid>
      </div>

      {/* Pipeline latency + service table */}
      <div className={classes.section}>
        <Grid container spacing={2}>
          {/* Latency bar chart */}
          <Grid item xs={12} md={4}>
            <Paper className={classes.card} elevation={0}>
              <Typography className={classes.cardTitle}>Pipeline Latency</Typography>
              <Typography className={classes.cardSub}>Avg response time per stage</Typography>
              {loading ? (
                <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={22} /></div>
              ) : latencyData.length === 0 ? (
                <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#777', fontSize: 12 }}>No latency data</div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={130}>
                    <BarChart data={latencyData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                      <CartesianGrid stroke="#f0f0f0" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#555' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: '#555' }} axisLine={false} tickLine={false} width={40}
                        tickFormatter={function(v) { return (v / 1000).toFixed(1) + 's' }} />
                      <Tooltip content={<ChartTooltip formatter={fmtMs} />} />
                      <Bar dataKey="ms" radius={[5, 5, 0, 0]}>
                        {latencyData.map(function(entry, i) {
                          return <Cell key={i} fill={entry.color} />
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                    {latencyData.map(function(d) {
                      return (
                        <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
                          <div style={{ width: 8, height: 8, borderRadius: 2, background: d.color }} />
                          <span style={{ color: '#777' }}>{d.label}</span>
                          <span style={{ color: '#1a2540', fontWeight: 700, fontFamily: 'monospace' }}>{fmtMs(d.ms)}</span>
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </Paper>
          </Grid>

          {/* Service breakdown table */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} style={{ border: '1px solid #e8ecf4', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0f0f0' }}>
                <Typography className={classes.cardTitle}>Service Breakdown</Typography>
                <Typography className={classes.cardSub} style={{ marginBottom: 0 }}>Cost share per service in range</Typography>
              </div>
              {loading ? (
                <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></div>
              ) : svcRows.length === 0 ? (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: '#aaa', fontSize: 13 }}>No service data</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Service', 'Total Cost', '% Share', 'Avg/Conversation', 'Conversations'].map(function(h) {
                        return <th key={h} className={classes.th}>{h}</th>
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {svcRows.map(function(row) {
                      return (
                        <tr key={row.key} className={classes.trHover} style={{ borderLeft: '3px solid ' + row.color }}>
                          <td className={classes.td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ width: 8, height: 8, borderRadius: '50%', background: row.color }} />
                              <span style={{ fontWeight: 600, color: '#1a2540' }}>{row.label}</span>
                            </div>
                          </td>
                          <td className={classes.td} style={{ fontWeight: 700, color: '#1a2540', fontFamily: 'monospace' }}>{fmtCost(row.cost)}</td>
                          <td className={classes.td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ flex: 1, height: 4, borderRadius: 999, background: '#f0f0f0', overflow: 'hidden', maxWidth: 80 }}>
                                <div style={{ height: '100%', width: row.pct + '%', background: row.color, borderRadius: 999 }} />
                              </div>
                              <span style={{ fontSize: 12, color: '#333', minWidth: 36 }}>{row.pct.toFixed(1)}%</span>
                            </div>
                          </td>
                          <td className={classes.td} style={{ fontSize: 12, color: '#333', fontFamily: 'monospace' }}>{fmtCost(row.avgPerSession)}</td>
                          <td className={classes.td} style={{ fontSize: 12, color: '#333', textAlign: 'center' }}>{row.sessionsUsed}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </Paper>
          </Grid>
        </Grid>
      </div>
    </div>
  )
}
